import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Clock, Smartphone } from 'lucide-react';
import { PageHeader } from '@/components/app/PageHeader';
import { LoadingState } from '@/components/app/LoadingState';
import { ErrorState } from '@/components/app/ErrorState';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { apiClient, humanizeError } from '@/lib/apiClient';
import { toast } from '@/hooks/useToast';

interface VersionRule {
  platform: 'android' | 'ios' | 'web';
  minimumSupportedVersion: string;
  latestAvailableVersion: string;
  forcedUpdateVersions?: string[];
  storeUrl?: string;
  enabled: boolean;
  updatedAt: string;
}

interface CronEntry {
  name: string;
  schedule: string;
  description: string;
  lastRun?: string;
  nextRun?: string;
  status?: string;
}

const api = {
  versionRules: () => apiClient.get<{ rules: VersionRule[] }>('/system/version-rules'),
  updateVersionRule: (platform: string, body: Partial<VersionRule>) =>
    apiClient.put<{ rule: VersionRule }>(
      `/system/version-rules/${encodeURIComponent(platform)}`,
      body,
    ),
  crons: () => apiClient.get<{ crons: CronEntry[] }>('/system/crons'),
};

export function SystemPage() {
  return (
    <div className="space-y-4">
      <PageHeader
        title="System"
        description="Version gating and scheduled-job registry."
      />
      <Tabs defaultValue="versions">
        <TabsList>
          <TabsTrigger value="versions">
            <Smartphone className="mr-1 h-4 w-4" /> Version rules
          </TabsTrigger>
          <TabsTrigger value="crons">
            <Clock className="mr-1 h-4 w-4" /> Crons
          </TabsTrigger>
        </TabsList>
        <TabsContent value="versions"><VersionRulesTab /></TabsContent>
        <TabsContent value="crons"><CronsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function VersionRulesTab() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['system', 'version-rules'],
    queryFn: () => api.versionRules(),
    staleTime: 60_000,
  });

  if (q.isLoading) return <LoadingState />;
  if (q.isError) return <ErrorState title="Version rules failed" description={humanizeError(q.error)} />;
  const rules = q.data?.rules ?? [];

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
      {rules.map((r) => (
        <VersionRuleCard key={r.platform} rule={r} onSaved={() => qc.invalidateQueries({ queryKey: ['system', 'version-rules'] })} />
      ))}
    </div>
  );
}

function VersionRuleCard({ rule, onSaved }: { rule: VersionRule; onSaved: () => void }) {
  const [minVer, setMinVer] = React.useState(rule.minimumSupportedVersion);
  const [latestVer, setLatestVer] = React.useState(rule.latestAvailableVersion);
  const [enabled, setEnabled] = React.useState(rule.enabled);
  const [storeUrl, setStoreUrl] = React.useState(rule.storeUrl ?? '');
  const mut = useMutation({
    mutationFn: () =>
      api.updateVersionRule(rule.platform, {
        minimumSupportedVersion: minVer,
        latestAvailableVersion: latestVer,
        enabled,
        storeUrl: storeUrl || undefined,
      }),
    onSuccess: () => {
      onSaved();
      toast({ title: `${rule.platform} rule saved`, variant: 'success' });
    },
    onError: (err) =>
      toast({ title: 'Save failed', description: humanizeError(err), variant: 'destructive' }),
  });

  const dirty =
    minVer !== rule.minimumSupportedVersion ||
    latestVer !== rule.latestAvailableVersion ||
    enabled !== rule.enabled ||
    (storeUrl || '') !== (rule.storeUrl || '');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between text-base capitalize">
          {rule.platform}
          {rule.enabled ? <Badge variant="success">Enabled</Badge> : <Badge variant="outline">Disabled</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <div className="space-y-1">
          <Label htmlFor={`${rule.platform}-min`}>Minimum supported</Label>
          <Input
            id={`${rule.platform}-min`}
            value={minVer}
            onChange={(e) => setMinVer(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor={`${rule.platform}-latest`}>Latest available</Label>
          <Input
            id={`${rule.platform}-latest`}
            value={latestVer}
            onChange={(e) => setLatestVer(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor={`${rule.platform}-store`}>Store URL</Label>
          <Input
            id={`${rule.platform}-store`}
            value={storeUrl}
            onChange={(e) => setStoreUrl(e.target.value)}
            placeholder="https://..."
          />
        </div>
        <div className="flex items-center gap-2 pt-1">
          <Switch
            id={`${rule.platform}-enabled`}
            checked={enabled}
            onCheckedChange={setEnabled}
          />
          <Label htmlFor={`${rule.platform}-enabled`}>Enabled</Label>
        </div>
        <Button
          size="sm"
          className="mt-2 w-full"
          disabled={!dirty || mut.isPending}
          onClick={() => mut.mutate()}
        >
          {mut.isPending ? 'Saving…' : 'Save'}
        </Button>
        <p className="text-xs text-muted-foreground">
          Updated {rule.updatedAt.slice(0, 10)}
        </p>
      </CardContent>
    </Card>
  );
}

function CronsTab() {
  const q = useQuery({
    queryKey: ['system', 'crons'],
    queryFn: () => api.crons(),
    staleTime: 60_000,
  });

  if (q.isLoading) return <LoadingState />;
  if (q.isError) return <ErrorState title="Crons failed" description={humanizeError(q.error)} />;
  const crons = q.data?.crons ?? [];

  return (
    <Card>
      <CardContent className="p-0">
        {crons.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-muted-foreground">
            No crons registered.
          </p>
        ) : (
          <ul className="divide-y text-sm">
            {crons.map((c) => (
              <li key={c.name} className="flex items-center justify-between px-4 py-3">
                <div className="min-w-0">
                  <div className="truncate font-medium">{c.name}</div>
                  <div className="truncate text-xs text-muted-foreground">
                    <code>{c.schedule}</code> · {c.description}
                  </div>
                  {(c.lastRun || c.nextRun) && (
                    <div className="truncate text-xs text-muted-foreground">
                      {c.lastRun && `last ${c.lastRun.slice(0, 19).replace('T', ' ')}`}
                      {c.lastRun && c.nextRun && ' · '}
                      {c.nextRun && `next ${c.nextRun.slice(0, 19).replace('T', ' ')}`}
                    </div>
                  )}
                </div>
                {c.status && (
                  <Badge
                    variant={
                      c.status === 'healthy' || c.status === 'ok' ? 'success' : 'warning'
                    }
                  >
                    {c.status}
                  </Badge>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
